/* OnSumn — EventsGrid components: CategoryCarouselRow, SkeletonCard, EmptyState
   Extracted from index.html. Behavior unchanged. */

/* ───── Skeleton loading card ───── */
function SkeletonCard({ darkMode, radius }) {
  const bg = darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
  return (
    <div className="skeleton-card" style={{
      ...glassStyle(darkMode, 'card'),
      border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.04)',
      borderRadius: radius, overflow: 'hidden',
    }}>
      <div style={{ height: 120, background: bg }} />
      <div style={{ padding: '12px 14px 14px' }}>
        <div style={{ height: 10, width: '40%', background: bg, borderRadius: 4, marginBottom: 12 }} />
        <div style={{ height: 16, width: '80%', background: bg, borderRadius: 4, marginBottom: 8 }} />
        <div style={{ height: 12, width: '100%', background: bg, borderRadius: 4, marginBottom: 6 }} />
        <div style={{ height: 12, width: '60%', background: bg, borderRadius: 4, marginBottom: 20 }} />
        <div style={{ height: 1, background: bg, marginBottom: 12 }} />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ height: 12, width: '30%', background: bg, borderRadius: 4 }} />
          <div style={{ height: 12, width: '25%', background: bg, borderRadius: 4 }} />
        </div>
      </div>
    </div>
  );
}

/* ───── Empty state ───── */
function EmptyState({ darkMode, radius, city, category }) {
  const cityName = city && city !== 'ALL' ? city.charAt(0) + city.slice(1).toLowerCase() : 'the Bay';
  const catName = category ? category.charAt(0) + category.slice(1).toLowerCase() : null;
  const headline = catName
    ? `No ${catName.toLowerCase()} in ${cityName}? We just ain't found it yet.`
    : `${cityName}'s quiet right now. That never lasts.`;
  const sub = 'Widen the filter or pull up tomorrow. The Bay always delivers.';

  return (
    <div style={{
      padding: '64px 32px', textAlign: 'center',
      ...glassStyle(darkMode, 'panel'),
      border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.04)',
      borderRadius: radius,
    }}>
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'}
        strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 16px', display: 'block' }}>
        <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
      </svg>
      <div style={{
        fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700,
        color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.55)', marginBottom: 8,
      }}>{headline}</div>
      <div style={{
        fontFamily: 'var(--font-body)', fontSize: 13,
        color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.55)',
      }}>{sub}</div>
    </div>
  );
}

/* ───── Category Carousel Row (mobile sectioned browsing) ─────
   Canonical color+icon map is in lib/category-colors.js (window.CATEGORY_META).
   Pre-fix this file had CARDS=#395542 and COMMUNITY=#7A99AC — both drifted
   from Flows. Local fallback below mirrors the canonical values so cold-
   cache loads still render the right hex. */
const CATEGORY_META = (typeof window !== 'undefined' && window.CATEGORY_META) || {
  'YOUR WAVE': { color: '#D57800', icon: '★' },
  'MUSIC': { color: '#FF4F00', icon: '♫' },
  'CARDS': { color: '#7A99AC', icon: '♠' },
  'ART': { color: '#5A7A68', icon: '◆' },
  'FOOD': { color: '#D57800', icon: '●' },
  'NIGHTLIFE': { color: '#7C5BBC', icon: '☽' },
  'COMMUNITY': { color: '#395542', icon: '◎' },
};

/* Always-callable hook shims — wrap window globals so the underlying
   hook is invoked unconditionally (no Rules-of-Hooks lint warning).
   Jace QA on PR #50 flagged the prior conditional-hook pattern. */
function useChunkedRenderSafeLocal(total, opts) {
  if (typeof window === 'undefined') return total | 0;
  if (typeof window.useChunkedRenderSafe === 'function') {
    return window.useChunkedRenderSafe(total, opts);
  }
  if (typeof window.useChunkedRender === 'function') {
    return window.useChunkedRender(total, opts);
  }
  return total | 0;
}
function useVisibilityRenderSafeLocal(total, opts) {
  if (typeof window === 'undefined' || typeof window.useVisibilityRenderSafe !== 'function') {
    return { isVisible: () => true, registerRef: () => () => {} };
  }
  return window.useVisibilityRenderSafe(total, opts);
}

/* GatedCarouselCard — IO-gated wrapper around .carousel-card + EventCard.
   Above-fold cards (index < initial) mount sync; below-fold cards stay
   as a min-width placeholder until their wrapper enters the horizontal
   scroll viewport (rootMargin: '300px' pre-mounts). The placeholder
   preserves carousel scroll-snap alignment so a fast horizontal scroll
   doesn't hit a hard edge mid-fill. */
function GatedCarouselCard({ event, darkMode, index, radius, taste, venueHeat, isVisible, registerRef, onCategoryClick, emphasis, hideOccasionKicker }) {
  const visible = isVisible(index);
  if (!visible) {
    return (
      <div
        ref={registerRef(index)}
        className="carousel-card"
        aria-hidden="true"
        style={{
          minHeight: 220,
          borderRadius: radius,
          background: darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
        }}
      />
    );
  }
  return (
    <div ref={registerRef(index)} className="carousel-card">
      <EventCard event={event} darkMode={darkMode} index={index} radius={radius} taste={taste} venueHeat={venueHeat} onCategoryClick={onCategoryClick} emphasis={emphasis} hideOccasionKicker={hideOccasionKicker} />
    </div>
  );
}

/* emphasisRole (Von Restorff wiring — EventsMapSection decides, rows obey):
     'lead'    → this row is the FIRST rendered row of the grid feed; its
                 index-0 card is the ONE hero of the entire feed, every
                 other card in the row is 'quiet'. The hero splits into two
                 presentation variants by WHICH row leads: 'hero-live' when
                 this row is YOUR WAVE (taste set — the "for you" claim is
                 true, so the card breathes), 'hero' otherwise (a category
                 merely leads — static; the page hasn't earned "you" yet).
                 Same glass surface either way; only the ::before breath
                 differs (GlassTokens injected sheet).
     'field'   → every card in the row is 'quiet' (a hero exists in some
                 other row above).
     undefined → legacy: no emphasis pass-through, cards render the
                 original glass surface (split view, sparse feeds, any
                 other call site).
   An occasion rail (Map.jsx) sits ABOVE the lead row and always gets
   'field' or undefined, so the one hero can sit in the row below it. */

/* ── OCCASION HEADER ── the seasonal rail's header (Zora spec §1, R14,
   2026-10-01), selected by CategoryCarouselRow's optional `occasion` prop
   ({ slug, label, total }); without the prop a row renders exactly as
   before. Anatomy: amber bar · h3 · hairline rule · NN SHOWS · 44px hub
   link. No drag handle: the rail is pinned above YOUR WAVE and never joins
   the reorder set, so the handle's box moves to the trailing edge and links
   to the hub instead. `total` is UNCAPPED: the rail paints at most 12 cards,
   the count says how deep the hub goes. Breakpoints (< 480 stacked meta,
   >= 480 one line, h3 18px at >= 768) and the link's hover/focus-visible
   state live in index.html (.occasion-rail-*) because inline styles cannot
   hold a media query; the theme inks are colors_and_type.css tokens
   (--on-surface, --occ-rail-rule, --occ-link-bg, --occ-link-bg-on), so the
   header carries no darkMode branch. The wrapper is <section aria-label> +
   data-occasion-rail (CategoryCarouselRow below). */
function OccasionRailHeader({ title, occasion, accent }) {
  const { slug, label, total } = occasion;
  const name = `All ${label} events`;
  return (
    <div className="occasion-rail-header" style={{ marginBottom: 12 }}>
      <div className="occasion-rail-main">
        <span className="occasion-rail-bar" aria-hidden="true" style={{
          width: 24, height: 2, flex: 'none', background: accent,
        }} />
        <h3 className="occasion-rail-title" style={{
          margin: 0, minWidth: 0, flex: '0 1 auto',
          fontFamily: 'var(--font-display)', fontWeight: 800, lineHeight: 1.15,
          letterSpacing: '0.08em', textTransform: 'uppercase',
          color: 'var(--on-surface)',
        }}>{title}</h3>
        <span className="occasion-rail-rule" aria-hidden="true" style={{
          flex: '1 1 16px', minWidth: 16, height: 1,
          background: 'var(--occ-rail-rule)',
        }} />
        <span className="category-row-count occasion-rail-count" style={{
          flex: 'none', fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 700,
          letterSpacing: '0.12em', textTransform: 'uppercase', fontVariantNumeric: 'tabular-nums',
          color: 'var(--on-surface-muted)',
        }}>{`${String(total).padStart(2, '0')} ${total === 1 ? 'SHOW' : 'SHOWS'}`}</span>
      </div>
      <a
        className="occasion-rail-link focus-double-ring"
        href={`/${slug}`}
        aria-label={name}
        title={name}
        style={{
          flex: 'none', width: 44, height: 44, boxSizing: 'border-box', padding: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: 8, textDecoration: 'none',
          background: 'var(--occ-link-bg)',
          color: 'var(--occ-link-ink, var(--on-surface-muted))',
        }}
      >
        <span aria-hidden="true" style={{
          fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: 18, lineHeight: 1,
        }}>→</span>
      </a>
    </div>
  );
}
/* ── /OCCASION HEADER ── */

function CategoryCarouselRow({ title, events, darkMode, radius, taste, venueHeat, accentColor, rowRef, isLifted, onHandlePointerDown, onCategoryClick, emphasisRole, occasion }) {
  const scrollRef = React.useRef(null);
  // Progress fill is mutated via ref (not state) and uses transform: scaleX
  // for compositor-only updates. Animating `width` and re-rendering React
  // on every scroll event made the bar visibly lag the carousel — see the
  // ~120ms behind-finger gap on horizontal swipe on mobile. The new path
  // runs in rAF, never re-enters React, and stays glued to the scroll.
  const progressFillRef = React.useRef(null);
  const rafRef = React.useRef(0);

  // Issue #44 — TWO levers stack on this carousel:
  // 1. Chunked render (PR #50): first 6 cards paint sync, then drip 12
  //    per idle slot. Gates main-thread cost during boot.
  // 2. IO-gated mount (Option C, this PR): cards stay UNMOUNTED until
  //    their wrapper enters the viewport — most carousel cards are off-
  //    screen-right and never visited, so this cuts the DOM footprint
  //    by ~80% on a 1080-event homepage.
  // rootMargin '300px' pre-mounts cards just before they're seen, so a
  // medium-speed horizontal scroll doesn't reveal placeholders.
  const reducedMotion = typeof window !== 'undefined' && typeof window.useReducedMotion === 'function'
    ? window.useReducedMotion()
    : false;
  const visibleCount = useChunkedRenderSafeLocal(events.length, { initial: 6, chunkSize: 12, reducedMotion });
  const visibleEvents = visibleCount >= events.length ? events : events.slice(0, visibleCount);
  const stillFilling = !reducedMotion && visibleCount < events.length;
  // IO layer — gates DOM mount of below-fold cards in the visible slice.
  const viz = useVisibilityRenderSafeLocal(visibleCount, { initial: 6, rootMargin: '300px', reducedMotion });

  const onScroll = React.useCallback(() => {
    // Coalesce to one update per animation frame. Scroll events can fire
    // many times per frame on trackpads/touchscreens; doing a transform
    // write per event burns the compositor and creates the lag.
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      const el = scrollRef.current;
      const fill = progressFillRef.current;
      if (!el || !fill) return;
      const max = el.scrollWidth - el.clientWidth;
      const p = max > 0 ? el.scrollLeft / max : 0;
      // 0.1 baseline keeps the fill visible even at scroll=0 (matches the
      // prior `Math.max(10, progress * 100)` % width behavior).
      fill.style.transform = `scaleX(${Math.max(0.1, p)})`;
    });
  }, []);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) { el.addEventListener('scroll', onScroll, { passive: true }); onScroll(); }
    return () => {
      if (el) el.removeEventListener('scroll', onScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
    };
  }, [events, visibleCount, onScroll]);

  if (!events.length) return null;

  const meta = CATEGORY_META[title] || { color: accentColor || '#FF4F00', icon: '·' };
  // An occasion rail is a named region (Iris, spec §7); a category row stays a <div>.
  const RowTag = occasion ? 'section' : 'div';

  return (
    <RowTag
      ref={rowRef}
      className="category-row"
      data-cat={title}
      data-occasion-rail={occasion ? occasion.slug : undefined}
      aria-label={occasion ? title : undefined}
      style={{
        transition: isLifted ? 'none' : 'transform 0.3s cubic-bezier(0.2, 0, 0, 1)',
        zIndex: isLifted ? 50 : 1,
        position: 'relative',
      }}
    >
      {occasion ? (
        <OccasionRailHeader title={title} occasion={occasion} accent={meta.color} />
      ) : (
      <div className="category-row-header">
        <div className="category-row-title">
          {/* Drag handle — works on mouse, pen, and touch.
              touchAction: 'none' on the handle blocks page scroll when the
              user starts a gesture here so the row can lift cleanly.
              Map.jsx's handlePointerDown gates touch with a 6px threshold
              so a stray tap doesn't accidentally trigger a reorder. */}
          <div
            className="reorder-handle"
            onPointerDown={(e) => onHandlePointerDown && onHandlePointerDown(e, title)}
            style={{
              cursor: 'grab', padding: 10, marginRight: 6,
              color: darkMode ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 8,
              background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
              transition: 'color 0.2s, background 0.2s',
              touchAction: 'none',
              userSelect: 'none',
              /* Larger hit area for touch — 38×38 is close to the iOS 44×44
                 floor while staying compact enough not to dominate the row. */
              minWidth: 38, minHeight: 38, boxSizing: 'border-box',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.color = darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)';
              e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.color = darkMode ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.25)';
              e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)';
            }}
            title="Drag to reorder"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <rect x="7" y="3" width="4" height="2.5" rx="1.25"/>
              <rect x="13" y="3" width="4" height="2.5" rx="1.25"/>
              <rect x="7" y="8" width="4" height="2.5" rx="1.25"/>
              <rect x="13" y="8" width="4" height="2.5" rx="1.25"/>
              <rect x="7" y="13" width="4" height="2.5" rx="1.25"/>
              <rect x="13" y="13" width="4" height="2.5" rx="1.25"/>
              <rect x="7" y="18" width="4" height="2.5" rx="1.25"/>
              <rect x="13" y="18" width="4" height="2.5" rx="1.25"/>
            </svg>
          </div>
          <div className="category-row-accent" style={{ background: meta.color }} />
          <span style={{
            fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700,
            color: darkMode ? '#fff' : '#1a1a1a',
            letterSpacing: '0.02em',
          }}>{title}</span>
          <span className="category-row-count" style={{
            color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.55)',
          }}>{events.length}</span>
        </div>
      </div>
      )}

      <div ref={scrollRef} className="events-carousel">
        {visibleEvents.map((e, i) => (
          <GatedCarouselCard
            key={e.id}
            event={e}
            darkMode={darkMode}
            index={i}
            radius={radius}
            taste={taste}
            venueHeat={venueHeat}
            isVisible={viz.isVisible}
            registerRef={viz.registerRef}
            onCategoryClick={onCategoryClick}
            hideOccasionKicker={occasion ? occasion.slug : undefined}
            emphasis={emphasisRole === 'lead'
              ? (i === 0 ? (title === 'YOUR WAVE' ? 'hero-live' : 'hero') : 'quiet')
              : (emphasisRole === 'field' ? 'quiet' : undefined)}
          />
        ))}
        {/* Ambient end-of-carousel placeholder while idle-chunks are still
            paying in. Renders a single subtle "MORE LOADING" tile so the
            horizontal-scroll affordance stays consistent for users who
            scroll fast — they won't hit a hard edge mid-fill. Vanishes
            silently once the row's full event set is on screen. */}
        {stillFilling && (
          <div
            key="__chunked-more"
            className="carousel-card"
            role="status"
            aria-live="polite"
            aria-atomic="true"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              minHeight: 160,
              ...glassStyle(darkMode, 'card'),
              border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.04)',
              borderRadius: radius,
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 700,
              letterSpacing: '0.12em', textTransform: 'uppercase',
              color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.55)',
            }}>
              <span
                aria-hidden="true"
                style={{
                  display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
                  background: meta.color,
                  animation: 'chunkedRenderPulse 1.4s ease-in-out infinite',
                }}
              />
              {`Loading · ${visibleCount}/${events.length}`}
            </div>
          </div>
        )}
      </div>

      {/* Scroll progress bar — width fixed at 100%, transform: scaleX
          driven by ref + rAF for compositor-only updates. Origin left so
          the fill grows from the start, not from center. */}
      {events.length > 2 && (
        <div className="scroll-progress-track" style={{
          background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
        }}>
          <div ref={progressFillRef} className="scroll-progress-fill" style={{
            width: '100%',
            transform: 'scaleX(0.1)',
            transformOrigin: 'left center',
            background: meta.color,
            opacity: 0.6,
            willChange: 'transform',
            transition: 'transform 80ms linear',
          }} />
        </div>
      )}
    </RowTag>
  );
}

window.SkeletonCard = SkeletonCard;
window.EmptyState = EmptyState;
window.GatedCarouselCard = GatedCarouselCard;
window.CategoryCarouselRow = CategoryCarouselRow;
// window.CATEGORY_META is now owned by lib/category-colors.js — removed the
// re-assign that previously overwrote the lib's value with this file's
// drifted local table.
